import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  ButtonBase,
  CircularProgress,
} from '@mui/material';
import { Check } from 'lucide-react';
import { FloatingLabelInput } from '../../../../../../../ui/components/Common/TextFields/OutlinedInput';
import {
  WellnessAssessmentType,
  WellnessConfig,
} from '../../../../../../../utils/types/wellness_types';
import { WELLNESS_ASSESSMENT_OPTIONS } from '../../../Constants';

interface SettingsTabProps {
  config: WellnessConfig;
  onChange: (updated: WellnessConfig) => void;
  onSave: () => void;
  isSaving: boolean;
}

const ASSESSMENT_DESCRIPTIONS: Record<WellnessAssessmentType, string> = {
  DASS21: 'Depression Anxiety Stress Scales',
  GAD7: 'Generalised Anxiety Disorder',
  PHQ9: 'Patient Health Questionnaire',
};

export const SettingsTab: React.FC<SettingsTabProps> = ({
  config,
  onChange,
  onSave,
  isSaving,
}) => {
  const handleDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const days = parseInt(e.target.value, 10);
    onChange({ ...config, dayLimit: isNaN(days) ? 0 : days });
  };

  const handleAssessmentToggle = (type: WellnessAssessmentType) => {
    const current = config.types;
    const next = current.includes(type)
      ? current.filter((t) => t !== type)
      : [...current, type];
    onChange({ ...config, types: next });
  };

  const isValid =
    config.types.length > 0 && config.dayLimit >= 1;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'grey.200',
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      {/* Days to Deadline */}
      <Box>
        <Typography variant='subtitle2' sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
          Wellness Screening Days to Deadline
        </Typography>
        <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
          Wellness invitation link expires{' '}
          <Typography component='span' variant='caption' sx={{ fontWeight: 600, color: 'text.primary' }}>
            {config.dayLimit > 0 ? config.dayLimit : '—'}
          </Typography>{' '}
          day{config.dayLimit !== 1 ? 's' : ''} after sending
        </Typography>
        <Box sx={{ maxWidth: 160 }}>
          <FloatingLabelInput
            label='Days'
            type='number'
            value={config.dayLimit > 0 ? String(config.dayLimit) : ''}
            onChange={handleDaysChange}
            placeholder='e.g. 7'
            error={config.dayLimit < 1 && config.dayLimit !== 0}
            helperText='Must be at least 1'
          />
        </Box>
      </Box>

      {/* Assessment Type Options */}
      <Box>
        <Typography variant='subtitle2' sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
          Wellness Screening Options
        </Typography>
        <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
          Select at least one assessment to include in the wellness screening
        </Typography>
        <Grid container spacing={2}>
          {WELLNESS_ASSESSMENT_OPTIONS.map((option) => {
            const type = option.value as WellnessAssessmentType;
            const checked = config.types.includes(type);
            return (
              <Grid item xs={12} sm={4} key={type}>
                <ButtonBase
                  onClick={() => handleAssessmentToggle(type)}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: checked ? 'primary.main' : 'grey.200',
                    bgcolor: checked ? 'action.hover' : 'background.paper',
                    p: 2,
                    textAlign: 'left',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: 'primary.light',
                    },
                  }}
                >
                  {/* Custom checkbox box */}
                  <Box
                    sx={{
                      mt: 0.25,
                      width: 18,
                      height: 18,
                      flexShrink: 0,
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid',
                      ...(checked
                        ? {
                            bgcolor: 'primary.main',
                            borderColor: 'primary.main',
                            color: 'common.white',
                          }
                        : {
                            bgcolor: 'background.paper',
                            borderColor: 'grey.300',
                          }),
                    }}
                  >
                    {checked && <Check size={12} />}
                  </Box>
                  <Box>
                    <Typography variant='body2' sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {type}
                    </Typography>
                    <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                      {ASSESSMENT_DESCRIPTIONS[type]}
                    </Typography>
                  </Box>
                </ButtonBase>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Save */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant='contained'
          onClick={onSave}
          disabled={!isValid || isSaving}
          startIcon={isSaving ? <CircularProgress size={16} color='inherit' /> : null}
          sx={{ borderRadius: 2, textTransform: 'none', px: 3, py: 1 }}
        >
          {isSaving ? 'Saving…' : 'Save Settings'}
        </Button>
      </Box>
    </Paper>
  );
};
